import React from "react";
import logo from "../../../../core/img/texas-alarms-logo.jpg";

// Single, reusable printable-document template shared by LPO/Stock-In/
// Stock-Out/Stock Return. Modeled directly on Working_Files/texas_LPO_modal.pdf
// (company header, party block, Sl No/Qty/Product Name/Price/VAT/Amount
// table with a "Grand Total in KSHS" footer row, a Delivery/Remarks blank-
// line section next to a boxed "quote LPO number" instruction, a supplier-
// identity line above signatures, totals, Prepared By/Approved By, footer
// address) - that PDF is the only real sample we have and this template
// matches it element-for-element for LPO. Stock-In/Out/Return reuse the same
// core layout (header/party/table/totals/signatures/footer-address) with
// showVat=false, an adjusted title, and showLpoFooterBlock=false (the
// delivery/quote-box/supplier-identity section is only confirmed for LPO)
// until real samples for those three arrive.
//
// This component renders identically whether shown live (the in-app Preview
// modal) or converted to a static HTML string for the print popup
// (StockDocumentPrintModal uses ReactDOMServer.renderToStaticMarkup on this
// same component) - one template, two render paths, never duplicated.
const money = (n) => (Number(n) || 0).toFixed(2);

// The print popup is opened via window.open("", "_blank") with no real URL
// (about:blank), so a webpack-emitted root-relative logo path (e.g.
// "/static/media/...jpg") would fail to resolve there even though it works
// fine in the live app - always resolve to a full absolute URL up front so
// both render paths (live preview and the static-markup popup) work
// identically.
const logoUrl = new URL(logo, window.location.origin).href;

const cell = { border: "1px solid #000", padding: 4 };

// Blank filler rows pad the item table out to a consistent height so a
// short LPO still fills a proper A4 page (matching the reference PDF's own
// look, which has trailing blank rows) instead of leaving a document that's
// mostly empty space above the totals box. Once the real item count already
// reaches this many rows there's nothing to pad - the table is left to flow
// and paginate naturally across as many pages as it needs.
const FILLER_TARGET_ROWS = 12;

const StockDocumentPreview = ({
  title,
  docNo,
  docDate,
  refNo = "",
  companyName = "TEXAS ALARMS (K) LTD",
  branchLabel,
  partyLabel = "Party Name",
  partyName,
  partyAddress,
  partyMobile,
  remarks,
  // LPO only - shown in the header block, but only on the OFFICE COPY (see
  // copyLabel below), per direct instruction that these stay internal.
  purposeTypeLabel,
  purposeDescription,
  showVat = false,
  vatPercent = 0,
  // Stock-Out uses "Last Purchase Cost" here instead of "Price" - the value
  // shown is what was last paid for the product (see LastPurchasePriceButton
  // in LineItemsEditor.jsx), not a price charged to the recipient, and the
  // generic "Price" label was reading as confusing/misleading there.
  priceColumnLabel = "Price",
  lines = [],
  totals = { subTotal: 0, vatAmount: 0, netAmount: 0 },
  preparedByName,
  approvedByName,
  // Only confirmed against the real LPO sample - see the file-level comment.
  showLpoFooterBlock = false,
  footerAddressLines = [
    "P.O.BOX 95439-207, MOMBASA, KENYA, TEL:+254 722866339, EMAIL: info@texas-alarms.com",
  ],
  copyLabel,
}) => {
  const totalQty = lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0);
  const fillerCount = Math.max(0, FILLER_TARGET_ROWS - lines.length);
  const isOfficeCopy = copyLabel === "OFFICE COPY";

  return (
    <div style={{ fontFamily: "Arial, sans-serif", fontSize: 13, color: "#000", background: "#fff", padding: 16, position: "relative" }}>
      {copyLabel && (
        <div
          style={{
            position: "absolute",
            top: "40%",
            left: "10%",
            fontSize: 48,
            fontWeight: "bold",
            color: "rgba(0,0,0,0.12)",
            transform: "rotate(-30deg)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {copyLabel}
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ ...cell, padding: 8, width: 220 }}>
              <img src={logoUrl} alt="Texas Alarms" style={{ height: 50 }} />
            </td>
            <td style={{ ...cell, padding: 8, textAlign: "center", fontSize: 22, fontWeight: "bold" }}>
              {title}
              {branchLabel && <div style={{ fontSize: 12, fontWeight: "normal" }}>{branchLabel}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ ...cell, padding: 8, verticalAlign: "top", width: "55%" }}>
              <div>{partyLabel}: &nbsp; {partyName || "-"}</div>
              <div style={{ marginTop: 8 }}>Address: &nbsp; {partyAddress || ""}</div>
              <div style={{ marginTop: 20 }}>Mobile No: &nbsp; {partyMobile || ""}</div>
            </td>
            <td style={{ ...cell, padding: 8, verticalAlign: "top" }}>
              <div>No: <strong>{docNo}</strong></div>
              <div>Date: &nbsp; {docDate}</div>
              <div style={{ marginTop: 8 }}>Reference No: &nbsp; {refNo}</div>
              <div style={{ marginTop: 8 }}>Remarks</div>
              <div>{remarks}</div>
              {isOfficeCopy && purposeTypeLabel && (
                <div style={{ marginTop: 8 }}>Purpose: &nbsp; {purposeTypeLabel}</div>
              )}
              {isOfficeCopy && purposeDescription && (
                <div style={{ marginTop: 4 }}>Purpose Description: &nbsp; {purposeDescription}</div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", marginBottom: 8 }}>
        <thead style={{ display: "table-header-group" }}>
          <tr>
            <th style={{ ...cell, width: 36 }}>Sl No</th>
            <th style={{ ...cell, width: 70 }}>Quantity</th>
            <th style={{ ...cell, textAlign: "left" }}>Product Name</th>
            <th style={{ ...cell, width: 90 }}>{priceColumnLabel}</th>
            {showVat && <th style={{ ...cell, width: 90 }}>VAT</th>}
            <th style={{ ...cell, width: 100 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => (
            <tr key={idx} style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <td style={{ ...cell, textAlign: "center" }}>{idx + 1}</td>
              <td style={{ ...cell, textAlign: "center" }}>{money(line.qty)}</td>
              <td style={cell}>{line.productName}</td>
              <td style={{ ...cell, textAlign: "right" }}>{money(line.price)}</td>
              {showVat && <td style={{ ...cell, textAlign: "right" }}>{money(line.vatAmount)}</td>}
              <td style={{ ...cell, textAlign: "right" }}>{money(line.amount)}</td>
            </tr>
          ))}
          {Array.from({ length: fillerCount }).map((_, idx) => (
            <tr key={`filler-${idx}`} style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <td style={cell}>&nbsp;</td>
              <td style={cell}>&nbsp;</td>
              <td style={cell}>&nbsp;</td>
              <td style={cell}>&nbsp;</td>
              {showVat && <td style={cell}>&nbsp;</td>}
              <td style={cell}>&nbsp;</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
            <td style={cell} />
            <td style={{ ...cell, textAlign: "center" }}>{money(totalQty)}</td>
            <td style={{ ...cell, fontWeight: "bold", textAlign: "center" }} colSpan={showVat ? 3 : 2}>
              Grand Total in KSHS
            </td>
            <td style={{ ...cell, textAlign: "right", fontWeight: "bold" }}>{money(totals.netAmount)}</td>
          </tr>
        </tfoot>
      </table>

      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: "60%" }} />
            <td>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000" }}>
                <tbody>
                  {showVat && (
                    <>
                      <tr>
                        <td style={cell}>Sub Total</td>
                        <td style={{ ...cell, textAlign: "right" }}>{money(totals.subTotal)}</td>
                      </tr>
                      <tr>
                        <td style={cell}>VAT {vatPercent}%</td>
                        <td style={{ ...cell, textAlign: "right" }}>{money(totals.vatAmount)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td style={{ ...cell, fontWeight: "bold" }}>
                      {showVat ? "Total Inc VAT" : "Grand Total"}
                    </td>
                    <td style={{ ...cell, textAlign: "right", fontWeight: "bold" }}>
                      {money(totals.netAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      {showLpoFooterBlock && (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, pageBreakInside: "avoid", breakInside: "avoid" }}>
          <tbody>
            <tr>
              <td style={{ verticalAlign: "top", width: "60%", paddingRight: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  DELIVERY TO OUR OFFICE /DEPOT AT&nbsp;
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 260 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 380 }} />
                </div>
                <div>
                  REMARKS IF ANY&nbsp;
                  <span style={{ display: "inline-block", borderBottom: "1px solid #000", width: 300 }} />
                </div>
              </td>
              <td style={{ verticalAlign: "top" }}>
                <div style={{ border: "1px solid #000", padding: 8, textAlign: "center", fontWeight: "bold" }}>
                  PLEASE QUOTE LPO NUMBER
                  <br />
                  ON YOUR INVOICE, PAYMENT
                  <br />
                  CAN ONLY BE MADE AGAINST
                  <br />
                  OFFICIAL ORDER
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {showLpoFooterBlock && (
        <div style={{ borderTop: "2px solid #000", borderBottom: "1px solid #000", padding: "8px 0", marginBottom: 8, pageBreakInside: "avoid", breakInside: "avoid" }}>
          This Supplier must indicate the identity of {companyName} {branchLabel ? `(${branchLabel})` : ""} Officer who
          collects the goods by showing on the Delivery Note Or Invoice
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, pageBreakInside: "avoid", breakInside: "avoid" }}>
        <tbody>
          <tr>
            <td style={{ paddingTop: showLpoFooterBlock ? 0 : 24 }}>
              {showLpoFooterBlock && <>Date: &nbsp; _______________________<br /></>}
              Prepared By: &nbsp; {preparedByName || "_______________________"}
              <br />
              Approved By: &nbsp; {approvedByName || "_______________________"}
            </td>
            <td style={{ textAlign: "right", verticalAlign: "bottom" }}>
              For and on behalf of
              <br />
              <strong>{companyName}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ border: "1px solid #000", padding: 8, textAlign: "center", fontSize: 11, pageBreakInside: "avoid", breakInside: "avoid" }}>
        {showLpoFooterBlock && <div style={{ marginBottom: 4 }}>TO AVOID DELAY IN PAYMENTS PLEASE SUBMIT YOUR INVOICE TO</div>}
        {showLpoFooterBlock && <div style={{ fontWeight: "bold", marginBottom: 4 }}>{companyName} {branchLabel ? `(${branchLabel})` : ""}</div>}
        {footerAddressLines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}
      </div>
    </div>
  );
};

export default StockDocumentPreview;
